import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { useSearchParams } from 'react-router-dom';
import { auth } from '../Firebase';
import { useAuth } from '../Context/AuthContext';
import freePlanIcon from '../Assets/billing/free.png';
import diamondPlanIcon from '../Assets/billing/diamond.png';
import everAfterPlanIcon from '../Assets/billing/ever-after.png';
import bestValueHeart from '../Assets/billing/best-value.png';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentModal from './PaymentModal.js';
import { useNavigate } from 'react-router-dom';
import { LogToServer } from '../utils/utilities';
import './BillingWeb.css';

const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
if (!stripeKey) {
    throw new Error('Missing Stripe publishable key');
}

const stripePromise = loadStripe(stripeKey);

const PLAN_FREE = 'free';
const PLAN_DIAMOND = 'diamond';
const PLAN_EVER_AFTER = 'ever_after';
const PLAN_PENDING = 'pending';

const PLAN_DETAILS = {
    [PLAN_FREE]: {
        title: 'Free',
        subtitle: 'Get started with the essentials',
        features: [
            'Guest list organisation',
            'Table planning',
            'Day schedule planner',
            'Job to do checklist',
            'Digital save the dates',
        ],
    },
    [PLAN_DIAMOND]: {
        title: 'Diamond',
        subtitle: 'Premium features with a flexible monthly plan',
        features: ['Personalised website', 'Guest photo gallery', 'Digital RSVP'],
    },
    [PLAN_EVER_AFTER]: {
        title: 'Ever After',
        subtitle: 'Lifetime access to premium features',
        features: ['Personalised website', 'Guest photo gallery', 'Digital RSVP', 'Lifetime access'],
    },
};

const normalizePlan = (rawPlan) => {
    if (!rawPlan) return PLAN_FREE;
    const plan = String(rawPlan).toLowerCase();
    if (plan.includes('ever')) return PLAN_EVER_AFTER;
    if (plan.includes('diamond')) return PLAN_DIAMOND;
    if (plan.includes('pending')) return PLAN_PENDING;
    return PLAN_FREE;
};

export default function BillingWeb() {
    const { currentUser } = useAuth();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [statusLoading, setStatusLoading] = useState(true);
    const [statusError, setStatusError] = useState('');
    const [subscriptionPlan, setSubscriptionPlan] = useState(PLAN_FREE);
    const [isSubscribed, setIsSubscribed] = useState(false);

    const [showLoginModal, setShowLoginModal] = useState(false);
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginError, setLoginError] = useState('');
    const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
    const [passwordResetMessage, setPasswordResetMessage] = useState('');

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [clientSecret, setClientSecret] = useState(null);
    const [loadingPayment, setLoadingPayment] = useState(false);

    const [pricesLoaded, setPricesLoaded] = useState(false);
    const [prices, setPrices] = useState(null);

    // only set payment redirect and used return url
    const redirectStatus = searchParams.get('redirect_status');

    useEffect(() => {
        if (!redirectStatus) return;

        // capture the rare case where we were redirected
        // to the billing page after payment
        if (redirectStatus === 'succeeded') {
            window.alert('You have successfully subscribed.');
        } else {
            window.alert(
                `Payment not completed (${redirectStatus}). Please contact support.`
            );
        }
    }, [redirectStatus]);

    const fetchStatus = async () => {
        setStatusLoading(true);
        setStatusError('');
        try {
            const res = await axios.post(`${process.env.REACT_APP_API}/users/get-client-by-id`, {
                id: currentUser.uid,
            });
            const plan = normalizePlan(res?.data?.subscriptionInfo?.plan);
            setSubscriptionPlan(plan);
            setIsSubscribed(plan === PLAN_DIAMOND || plan === PLAN_EVER_AFTER);

            // ensure that the retrieved users email matches the uri parameter email
            // this confirms that a logged in user has not had their uri tampered
            // with at a later time
            const emailParam = searchParams.get('email');
            if (res?.data?.email !== emailParam) {
                setSubscriptionPlan(PLAN_FREE);
                setIsSubscribed(false);
                setStatusError('User mismatch. Please click through again from your email.');
            }
        } catch (error) {
            setStatusError('Unable to retrieve billing status right now.');
            setSubscriptionPlan(PLAN_FREE);
            setIsSubscribed(false);
        } finally {
            setStatusLoading(false);
        }
    };

    useEffect(() => {
        // Security consideration for a user that remains logged (on public device):
        //    - it does not confer any benefit to a subsequent user for their own subscription
        //    - they cannot have their subscription purposely changed because once subscribed
        //      it is read only
        //    - they could be subscribed by a subsequent user but they would still need card
        //      details.
        //    - in any case firebase auth should result in token refresh and logout after 1 hour TBD TEST
        if (!currentUser) {
            // Gather indication of how many hits we are getting to this promotion page
            const emailParam = searchParams.get('email');
            LogToServer(`Website BILLING PAGE HIT with URL email ${emailParam || 'unknown'}`)

            setShowLoginModal(true);
            setStatusLoading(false);
            setPricesLoaded(false);
            return;
        } else {
            // ensure that if the email in the uri has changed that
            // a re-auth via login modal is required
            const emailParam = searchParams.get('email');
            if (currentUser?.email !== emailParam) {
                setShowLoginModal(true);
            } else {
                // if we have an authd user and the email matches the
                // uri email, then set the loginEmail to that email
                setShowLoginModal(false);
                setLoginEmail(emailParam)
            }
        }

        LogToServer(`Website BILLING PAGE LOGIN for user ${currentUser?.email}`)

        const fetchPrices = async () => {
            setPricesLoaded(false);
            try {
                const res = await axios.post(`${process.env.REACT_APP_API}/subscriptions/client-pricing`, {
                    clientId: currentUser.uid,
                });
                setPrices(res.data);
                setPricesLoaded(true);
            } catch (error) {
                setStatusError('Unable to retrieve billing status right now.');
            }
        };

        fetchStatus();
        fetchPrices();
    }, [currentUser]);

    useEffect(() => {
        if (!showLoginModal) return;
        const fromQuery = searchParams.get('email');
        if (fromQuery) setLoginEmail(fromQuery);
    }, [searchParams, showLoginModal]);

    const currentPlanData = useMemo(() => {
        if (subscriptionPlan === PLAN_PENDING) {
            return {
                title: 'Subscription Update Pending',
                subtitle: 'No changes can be made at this time.',
                features: [],
            };
        }
        return PLAN_DETAILS[subscriptionPlan] || PLAN_DETAILS[PLAN_FREE];
    }, [subscriptionPlan]);

    const headerPlanLabel = useMemo(() => {
        if (subscriptionPlan === PLAN_DIAMOND) return 'Diamond';
        if (subscriptionPlan === PLAN_EVER_AFTER) return 'Ever After';
        if (subscriptionPlan === PLAN_PENDING) return 'Pending';
        return 'Free';
    }, [subscriptionPlan]);

    const headerPlanBadge = useMemo(() => {
        if (subscriptionPlan === PLAN_DIAMOND) return diamondPlanIcon;
        if (subscriptionPlan === PLAN_EVER_AFTER) return everAfterPlanIcon;
        return freePlanIcon;
    }, [subscriptionPlan]);

    const handleEverAfterPlanSelection = async () => {
        if (isSubscribed) {
            window.alert("You are already subscribed. Please manage any changes in the app.");
            return;
        }

        const plan = PLAN_EVER_AFTER;
        setSelectedPlan(plan);

        try {
            setLoadingPayment(true);

            const res = await axios.post(
                `${process.env.REACT_APP_API}/subscriptions/create-everafter-subscription-website`, {
                email: loginEmail,
                clientId: currentUser.uid,
            });

            setClientSecret(res.data.clientSecret);
            setShowPaymentModal(true);
        } catch (error) {
            window.alert("Failed to load payment modal. Please try again.");
            console.error('Failed to create payment intent', error);
            setClientSecret(null);
        } finally {
            setLoadingPayment(false);
        }
    };

    const handleDiamondPlanSelection = async () => {
        if (isSubscribed) {
            window.alert("You are already subscribed. Please manage any changes in the app.");
            return;
        }

        const plan = PLAN_DIAMOND;
        setSelectedPlan(plan);

        try {
            setLoadingPayment(true);

            const res = await axios.post(
                `${process.env.REACT_APP_API}/subscriptions/create-diamond-subscription-website`,
                {
                    email: loginEmail,
                    clientId: currentUser.uid,
                }
            );

            setClientSecret(res.data.clientSecret);
            setShowPaymentModal(true);
        } catch (error) {
            window.alert("Failed to load payment modal. Please try again.");
            console.error('Failed to create susbcription', error);
            setClientSecret(null);
        } finally {
            setLoadingPayment(false);
        }
    };

    const handleFreePlanSelection = async (plan) => {
        // Selecting the free plan is only possible for users on Diamond.
        // All subscription management including switching back to free
        // is to be managed in the app
        if (isSubscribed) {
            window.alert("You are already subscribed. Please manage any changes in the app.");
            return;
        }
    }

    const handlePaymentModalClose = async (subscribe_successful) => {
        setShowPaymentModal(false);
        setClientSecret(null);

        try {
            await fetchStatus();
            if (subscribe_successful === 'Subscribe Successful') {
                navigate(`/subscribed-feedback?email=${encodeURIComponent(currentUser.email)}`);
            }
        } catch (e) {
            window.alert("Subscription updated, but failed to refresh status.");
        }
    }

    const handleForgottenPassword = async () => {
        setLoginError('');
        setPasswordResetMessage('');
        const email = loginEmail.trim();
        if (!email) {
            setLoginError('Please enter your email address.');
            return;
        }
        setResetPasswordLoading(true);
        try {
            await sendPasswordResetEmail(auth, email);
            setPasswordResetMessage('Password reset email sent. Please check your inbox or spam folder.');
        } catch (error) {
            const codeMap = {
                'auth/invalid-email': 'Invalid email address.',
                'auth/too-many-requests': 'Too many attempts. Please try again later.',
            };
            setLoginError(codeMap[error.code] || 'Could not send reset email. Please try again.');
        } finally {
            setResetPasswordLoading(false);
        }
    };

    const login = async (e) => {
        e.preventDefault();
        setLoginLoading(true);
        setLoginError('');
        setPasswordResetMessage('');
        try {
            await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
        } catch (error) {
            const codeMap = {
                'auth/user-not-found': 'No account found with this email.',
                'auth/wrong-password': 'Incorrect password.',
                'auth/invalid-email': 'Invalid email address.',
                'auth/too-many-requests': 'Too many attempts. Please try again later.',
            };
            setLoginError(codeMap[error.code] || 'Login failed. Please try again.');
        } finally {
            setLoginLoading(false);
        }
    };

    const renderFeatureRows = (items, tickClass = '') =>
        items.map((item) => (
            <div key={item} className="billing-feature-row">
                <span className={`billing-feature-dot ${tickClass}`}>•</span>
                <span>{item}</span>
            </div>
        ));

    const everAfterPrice = () => {
        if ((prices?.everafter?.discounted ?? 0) > 0) {
            return (
                <>
                    Offer: One payment of £{prices.everafter.discounted}, normally £{prices.everafter.amount}.

                </>
            );
        } else {
            return (
                <>
                    One payment of £{prices.everafter.amount}
                </>
            );
        }
    }

    const diamondPrice = () => {
        if (((prices?.diamond?.discounted ?? 0) > 0) && (prices?.diamond?.discounted !== prices?.diamond?.amount)) {
            return (
                <>
                    Offer: 3 payments at £{prices.diamond.discounted}, then £{prices.diamond.amount} monthly
                </>
            );
        } else {
            return (
                <>
                    Pay £{prices.diamond.amount} monthly
                </>
            );
        }
    }

    const planCards = [
        {
            key: PLAN_EVER_AFTER,
            title: 'Ever After',
            price: pricesLoaded ? everAfterPrice() : 'Life-time plan: please refresh for price',
            subtitle: 'Lifetime access to the app*',
            features: PLAN_DETAILS[PLAN_EVER_AFTER].features,
            icon: everAfterPlanIcon,
            actionLabel: 'Upgrade to Ever After',
            action: () => handleEverAfterPlanSelection(),
            cardClass: 'billing-plan-card-ever-after',
            tickClass: 'is-light',
            showBestValue: true,
            iconClass: 'is-ever-after',
        },
        {
            key: PLAN_DIAMOND,
            title: 'Diamond',
            price: pricesLoaded ? diamondPrice() : 'Monthly plan: please refresh for price',
            subtitle: 'Unlock premium features with a flexible monthly plan',
            features: PLAN_DETAILS[PLAN_DIAMOND].features,
            icon: diamondPlanIcon,
            actionLabel: 'Switch to Diamond',
            action: () => handleDiamondPlanSelection(),
            cardClass: 'billing-plan-card-diamond',
            extraNote: 'Cancel any time',
            iconClass: 'is-diamond',
        },
        {
            key: PLAN_FREE,
            title: 'Free',
            price: '£0',
            subtitle: 'Get started with the essentials',
            features: PLAN_DETAILS[PLAN_FREE].features,
            icon: freePlanIcon,
            actionLabel: 'Select Free',
            action: () => handleFreePlanSelection(),
            cardClass: 'billing-plan-card-free',
            iconClass: 'is-free',
        },
    ];

    const orderedPlanCards = [...planCards].sort((a, b) => {
        if (a.key === subscriptionPlan) return -1;
        if (b.key === subscriptionPlan) return 1;
        return 0;
    });


    return (
        <>
            {loadingPayment && (
                <div className="loading-overlay">
                    <div className="spinner" />
                </div>
            )}

            <div className={`billing-web-page ${showLoginModal ? 'billing-web-blurred' : ''}`}>
                <header className="billing-web-header">
                    <div className="billing-web-header-inner">
                        <div className="billing-web-header-badge-row">
                            <img src={headerPlanBadge} alt="" className="billing-web-header-plan-badge" />
                            <div className="billing-web-header-text-column">
                                <p className="billing-web-header-title">Account</p>
                                <p className="billing-web-header-subtitle">{(statusLoading || !pricesLoaded) ? 'Loading...' : headerPlanLabel}</p>
                            </div>
                        </div>
                    </div>
                </header>
                <section className="billing-shell">
                    <h1 className="billing-page-title">Billing</h1>
                    <p className="billing-section-label">Select Plan</p>
                    <div className="billing-select-grid">
                        {orderedPlanCards.map((planCard, index) => (
                            <article
                                key={planCard.key}
                                className={`billing-plan-card ${planCard.cardClass} ${index === 0 ? 'billing-plan-card-current' : ''}`}
                            >
                                {planCard.showBestValue && (
                                    <img src={bestValueHeart} alt="Best value" className="billing-best-value-heart" />
                                )}
                                <h3 className="billing-plan-title">{planCard.title}</h3>
                                <p className="billing-plan-price">{planCard.price}</p>
                                <p className="billing-plan-subtitle">{planCard.subtitle}</p>
                                <div className="billing-plan-row">
                                    <div className="billing-feature-list">
                                        {renderFeatureRows(planCard.features, planCard.tickClass || '')}
                                        {planCard.extraNote && <p className="billing-cancel-anytime">{planCard.extraNote}</p>}
                                        {index === 0 && !statusLoading && (
                                            <p className="billing-status-note">Subscribed: {isSubscribed ? 'Yes' : 'No'}</p>
                                        )}
                                        {index === 0 && statusError && <p className="billing-error">{statusError}</p>}
                                    </div>
                                    <div className="billing-plan-icon-col">
                                        <img src={planCard.icon} alt="" className={`billing-plan-icon ${planCard.iconClass || ''}`} />
                                    </div>
                                </div>
                                {index !== 0 && (
                                    <button
                                        disabled={loadingPayment || statusError}
                                        onClick={planCard.action}>{planCard.actionLabel}
                                    </button>
                                )}
                            </article>
                        ))}
                    </div>
                </section>
            </div>

            {showLoginModal && (
                <div className="billing-login-overlay">
                    <div className="billing-login-modal">
                        <h2>Login to continue</h2>
                        <p>You need to be logged in to access billing on web.</p>
                        <form onSubmit={login}>
                            <label htmlFor="billing-email">Email</label>
                            <input
                                id="billing-email"
                                type="email"
                                value={loginEmail}
                                onChange={(e) => setLoginEmail(e.target.value)}
                                placeholder="Enter your email"
                                required
                                disabled={loginLoading || resetPasswordLoading}
                            />
                            <label htmlFor="billing-password">Password</label>
                            <input
                                id="billing-password"
                                type="password"
                                value={loginPassword}
                                onChange={(e) => setLoginPassword(e.target.value)}
                                placeholder="Enter your password"
                                required
                                disabled={loginLoading || resetPasswordLoading}
                            />
                            <button
                                type="button"
                                className="billing-forgot-password"
                                onClick={handleForgottenPassword}
                                disabled={loginLoading || resetPasswordLoading}
                            >
                                {resetPasswordLoading ? 'Sending...' : 'Forgotten password?'}
                            </button>
                            {passwordResetMessage && (
                                <p className="billing-reset-message">{passwordResetMessage}</p>
                            )}
                            {loginError && <p className="billing-error">{loginError}</p>}
                            <button type="submit" disabled={loginLoading || resetPasswordLoading}>
                                {loginLoading ? 'Logging in...' : 'Login'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {showPaymentModal && clientSecret && (
                <Elements stripe={stripePromise} options={{clientSecret}}>
                    <PaymentModal
                        open={showPaymentModal}
                        onClose={handlePaymentModalClose}
                        clientSecret={clientSecret}
                        price={selectedPlan === PLAN_EVER_AFTER ? everAfterPrice() : diamondPrice()}
                        email={loginEmail}
                    />
                </Elements>
            )}
        </>
    );
}
